/**
 * Inbound assets for intents (ADR-009 §5.1 rule 3).
 *
 * An intent names image bytes with an `AssetInput`. The dispatcher never
 * reads bytes itself: each intent that needs them calls `resolveAsset` while
 * it prepares (outside history, under `captureRevision`), and the result goes
 * through the editor's own codecs and preflight (`readImageSourceAsset`,
 * `draftImageDimensions`, `DEFAULT_LIMITS`) exactly as a file the user placed.
 *
 * Forms:
 * - `{ existing: assetId }`: an asset already in the document's resource table;
 * - `{ inlineBase64, mediaType }`: PNG, JPEG or WebP bytes, at most 10 MiB;
 * - `{ staged, mediaType, bytes, sha256 }`: bytes a transport delivered for
 *   this call ahead of it (the local bridge's chunked staging, which also
 *   turns `{ localPath }` and large inline images into this form). They are
 *   read from the call's `StagedBytesSource` (`PrepareContext.assets`); a
 *   caller without one (the in-page API, the UI) is refused;
 * - `{ localPath }`: never read in the tab (only the daemon reads files).
 *
 * The transport (fetching, chunking, digests over the wire) is not this
 * module's business: it only asks the source for verified bytes by id.
 */
import { BRIDGE_LIMITS, type AssetInput, type RejectionCode } from '@maipo/agent-protocol';
import type { PublicAsset } from '@maipo/format';
import { DEFAULT_LIMITS, validateDimensions } from '../core/model';
import type { DocumentSession } from '../core/session';
import type { DecodeBudget } from './budgets';
import { IntentRejection } from './registry';

/** Decoded bytes are capped at 10 MiB (§5.1); base64 for that is at most this long. */
export const INLINE_ASSET_LIMITS = Object.freeze({
  bytes: 10 * 1024 * 1024,
  base64Length: Math.ceil((10 * 1024 * 1024) / 3) * 4,
  mediaTypes: ['image/png', 'image/jpeg', 'image/webp'] as const,
});

/**
 * Verified bytes a transport delivered for one call, by staged id. The local
 * bridge's tab-side staged-asset store implements it (feat/agent-parity-assets-bridge,
 * `StagedAssetSource`); an expired or foreign id is `undefined`.
 */
export interface StagedBytesSource {
  get(id: string): { readonly mediaType: string; readonly sha256: string; readonly data: Uint8Array } | undefined;
}

/**
 * What resolution hands back.
 * - `existing`: an asset of the document's own resource table, used as is;
 * - `bytes`: encoded image bytes the editor admits like a placed file.
 *
 * `size` is the image's natural size from its container header (preflight),
 * known before any decoder runs.
 */
export type ResolvedAsset =
  | { kind: 'existing'; asset: PublicAsset; size: { width: number; height: number } }
  | { kind: 'bytes'; blob: Blob; size: { width: number; height: number } };

export interface AssetResolveContext {
  session: DocumentSession;
  signal: AbortSignal;
  /** The call's staged bytes; present only for calls from a transport that stages (the local bridge). */
  assets?: StagedBytesSource | undefined;
  /**
   * The transaction's decoded-byte budget (agents and add-ons). Resolving an
   * asset charges its natural decoded size, because every caller decodes it.
   */
  decode?: DecodeBudget | undefined;
}

function reject(code: RejectionCode, message: string): never {
  throw new IntentRejection(code, message);
}

function decodeBase64(text: string): Uint8Array {
  if (text.length % 4 !== 0 || !/^[A-Za-z0-9+/]*={0,2}$/.test(text)) reject('SCHEMA_INVALID', 'inlineBase64 must be standard, padded base64.');
  let binary = '';
  try { binary = atob(text); } catch { reject('SCHEMA_INVALID', 'inlineBase64 must be standard, padded base64.'); }
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

/**
 * Structural preflight before any decoder runs: the container must be the
 * declared still-image type, and its dimensions must fit the editor's
 * document limits.
 */
export async function preflightImageBytes(bytes: Uint8Array, mediaType: string): Promise<{ width: number; height: number }> {
  if (!(INLINE_ASSET_LIMITS.mediaTypes as readonly string[]).includes(mediaType)) reject('UNSUPPORTED_FORMAT', `Images must be PNG, JPEG or WebP (got ${JSON.stringify(mediaType).slice(0, 40)}).`);
  const { draftImageDimensions } = await import('../public-format/image-assets');
  let size: { width: number; height: number } = { width: 0, height: 0 };
  try { size = draftImageDimensions(bytes, mediaType); } catch (error) { reject('UNSUPPORTED_FORMAT', error instanceof Error ? error.message : 'The image could not be read.'); }
  try { validateDimensions(size.width, size.height); } catch (error) { reject('LIMIT_EXCEEDED', error instanceof Error ? error.message : 'The image is too large.'); }
  if (size.width * size.height * 4 > DEFAULT_LIMITS.maxDecodedBytes) reject('LIMIT_EXCEEDED', `The image would decode to more than ${DEFAULT_LIMITS.maxDecodedBytes} bytes.`);
  return size;
}

async function admitBytes(bytes: Uint8Array, mediaType: string, maxBytes: number, decode: DecodeBudget | undefined): Promise<ResolvedAsset> {
  if (bytes.byteLength === 0) reject('SCHEMA_INVALID', 'The image has no bytes.');
  if (bytes.byteLength > maxBytes) reject('LIMIT_EXCEEDED', `Images are limited to ${maxBytes} bytes.`);
  const size = await preflightImageBytes(bytes, mediaType);
  decode?.charge(size.width * size.height * 4, `Decoding a ${size.width} × ${size.height} image`);
  return { kind: 'bytes', blob: new Blob([bytes as BlobPart], { type: mediaType }), size };
}

function base64Bytes(text: string): Uint8Array {
  const binary = atob(text);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

/** The natural size of a document asset from its header (a prefix of its bytes first; JPEG headers can sit late). */
async function existingSize(asset: PublicAsset): Promise<{ width: number; height: number }> {
  const prefix = asset.data.length > 1_398_104 ? asset.data.slice(0, 1_398_104) : undefined;
  if (prefix) {
    try { return await preflightImageBytes(base64Bytes(prefix), asset.mediaType); } catch { /* the header is further in: read it all */ }
  }
  return preflightImageBytes(base64Bytes(asset.data), asset.mediaType);
}

/** Resolves one AssetInput for `prepare`. Refusals throw `IntentRejection`. */
export async function resolveAsset(input: AssetInput, context: AssetResolveContext): Promise<ResolvedAsset> {
  const record = input as unknown as Record<string, unknown>;
  if (!record || typeof record !== 'object' || Array.isArray(record)) return reject('SCHEMA_INVALID', 'An asset must be an object.');
  if (typeof record.existing === 'string') {
    const id = record.existing;
    const { documentPublicResources } = await import('../public-format/adapter');
    const asset = documentPublicResources(context.session.document).assets.find((candidate) => candidate.id === id);
    if (!asset) reject('COMMAND_REJECTED', `Asset ${JSON.stringify(id).slice(0, 80)} is not in this document (see the scene view's assets).`);
    if (!asset.mediaType.startsWith('image/')) reject('UNSUPPORTED_FOR_KIND', `Asset ${asset.id} is not an image.`);
    if (!context.decode) return { kind: 'existing', asset, size: { width: 0, height: 0 } };
    const size = await existingSize(asset);
    context.decode.charge(size.width * size.height * 4, `Decoding the ${size.width} × ${size.height} image ${asset.id}`);
    return { kind: 'existing', asset, size };
  }
  if (Object.hasOwn(record, 'staged')) {
    if (!context.assets) reject('PERMISSION_DENIED', 'A { staged } asset is delivered only by the local bridge, for the call that carries it.');
    const asset = typeof record.staged === 'string' ? context.assets.get(record.staged) : undefined;
    if (!asset || asset.mediaType !== record.mediaType || asset.sha256 !== record.sha256 || asset.data.byteLength !== record.bytes) reject('SCHEMA_INVALID', 'The staged asset is unknown to this call, expired, or does not match its reference.');
    // Staged bytes were capped per source by the bridge (10 MiB inline, 32 MiB from an import folder).
    return admitBytes(asset.data, asset.mediaType, BRIDGE_LIMITS.staging.localAssetBytes, context.decode);
  }
  if (typeof record.localPath === 'string') return reject('PERMISSION_DENIED', 'Files are read only by the local Maipo helper, inside folders the user allowed; this caller cannot pass { localPath }.');
  if (typeof record.inlineBase64 === 'string' && typeof record.mediaType === 'string') {
    if (record.inlineBase64.length > INLINE_ASSET_LIMITS.base64Length) reject('LIMIT_EXCEEDED', `Inline images are limited to ${INLINE_ASSET_LIMITS.bytes} bytes.`);
    if (!(INLINE_ASSET_LIMITS.mediaTypes as readonly string[]).includes(record.mediaType)) reject('UNSUPPORTED_FORMAT', `Inline images must be PNG, JPEG or WebP (got ${JSON.stringify(record.mediaType).slice(0, 40)}).`);
    return admitBytes(decodeBase64(record.inlineBase64), record.mediaType, INLINE_ASSET_LIMITS.bytes, context.decode);
  }
  return reject('SCHEMA_INVALID', 'Unknown asset form.');
}

/** Encoded bytes of a resolved asset, for decoders that need a Blob (raster layers, slot fills). */
export function resolvedBlob(resolved: ResolvedAsset): Blob {
  if (resolved.kind === 'bytes') return resolved.blob;
  return new Blob([base64Bytes(resolved.asset.data) as BlobPart], { type: resolved.asset.mediaType });
}

/** Turns pixels into a raster: decoder for raster layers and image slots, injectable for tests (no canvas in Node). */
export interface RasterImageDecoder {
  /**
   * Decodes `blob` to straight RGBA. Without `size`, at its own size. With
   * `size`, resampled to exactly that size: `cover` crops centred (the Fill
   * panel's slot fill), `exact` stretches.
   */
  decode(blob: Blob, size: { width: number; height: number; fit: 'cover' | 'exact' } | undefined, signal: AbortSignal): Promise<ImageData>;
  /** The decoded size before any resampling, for aspect-ratio sizing. */
  measure(blob: Blob, signal: AbortSignal): Promise<{ width: number; height: number }>;
}

function canvas2d(width: number, height: number): CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D {
  if (typeof OffscreenCanvas !== 'undefined') {
    const context = new OffscreenCanvas(width, height).getContext('2d');
    if (context) return context;
  }
  const element = globalThis.document.createElement('canvas');
  element.width = width;
  element.height = height;
  const context = element.getContext('2d');
  if (!context) throw new Error('Canvas 2D is unavailable.');
  return context;
}

/** The editor's own path: createImageBitmap plus a 2D canvas, as the Fill panel and dropped files use. */
export const BROWSER_RASTER_DECODER: RasterImageDecoder = {
  async measure(blob) {
    const bitmap = await createImageBitmap(blob);
    try { return { width: bitmap.width, height: bitmap.height }; } finally { bitmap.close(); }
  },
  async decode(blob, size) {
    const bitmap = await createImageBitmap(blob);
    try {
      const width = size?.width ?? bitmap.width;
      const height = size?.height ?? bitmap.height;
      const context = canvas2d(width, height);
      if (!size || size.fit === 'exact') {
        context.drawImage(bitmap, 0, 0, width, height);
      } else {
        const scale = Math.max(width / bitmap.width, height / bitmap.height);
        const drawWidth = bitmap.width * scale;
        const drawHeight = bitmap.height * scale;
        context.drawImage(bitmap, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
      }
      return context.getImageData(0, 0, width, height);
    } finally {
      bitmap.close();
    }
  },
};

let rasterDecoder: RasterImageDecoder = BROWSER_RASTER_DECODER;
export function rasterImageDecoder(): RasterImageDecoder { return rasterDecoder; }
/** Tests replace the decoder (Node has no canvas); returns a restore function. */
export function setRasterImageDecoder(decoder: RasterImageDecoder): () => void {
  const previous = rasterDecoder;
  rasterDecoder = decoder;
  return () => { rasterDecoder = previous; };
}
